import { forwardRef, type SVGAttributes } from 'react';

import { cn } from '../utils/cn';

import {
  MIN_WYSOKOSC_POZIOMEGO_PX,
  NAPIS_SZEROKOSC,
  NAPIS_WYSOKOSC,
  SIATKA_SYGNETU,
  SYGNET_UPROSZCZONY,
  UKLADY_LOGO,
  sciezkaSygnetu,
  sciezkaSygnetuUproszczonego,
} from './geometria';
import { SCIEZKA_NAPISU } from './napis';

// Skala znaku stoi w geometrii (czysty TS, czytany też przez generator),
// a subścieżka pakietu `@insolitum/ui/marka/*` mapuje wyłącznie pliki
// `.tsx` — więc aplikacje biorą ją stąd (albo z beczki `@insolitum/ui`):
//   import { SKALA_ZNAKU } from '@insolitum/ui/marka/LogoInsolitum';
export { SKALA_ZNAKU } from './geometria';

/**
 * Logo Insolitum — znak STATYCZNY, w czterech układach z README pakietu
 * (`docs/marka/insolitum/README.md`):
 *
 * | układ      | kiedy                                                                |
 * | ---------- | -------------------------------------------------------------------- |
 * | `poziome`  | domyślnie: nagłówek, stopka, dokumenty                               |
 * | `pionowe`  | miejsca kwadratowe i wąskie: logowanie, ekran startowy               |
 * | `sygnet`   | ikony, awatar, znak wodny, małe przestrzenie (≥ 24 px)               |
 * | `napis`    | tylko gdy sygnet stoi już w tym samym widoku                         |
 *
 * Kolor to `currentColor`: pozytyw i negatyw nie są dwiema wersjami
 * komponentu, tylko kolorem tekstu rodzica — na jasnym tle czerń, na ciemnym
 * biel, w motywie Aurora `var(--ids-text)`. Dzięki temu znak sam przechodzi
 * między trybami jasnym i ciemnym, bez drugiego pliku.
 *
 * Wysokość ustala się przez `wysokosc` (px) albo klasą (`h-8`); szerokość
 * wynika z proporcji układu. Proporcji nie wolno zmieniać, więc komponent nie
 * przyjmuje szerokości.
 *
 * Poniżej 32 px sygnet przechodzi sam na wersję uproszczoną (tarcza
 * i zewnętrzna obręcz) — pełny znak zlewa się w tak małym rozmiarze. Gdy
 * rozmiar ustala CSS (np. `.sidebar__mark svg{ width:22px }`), komponent tej
 * wysokości nie zna — wtedy wersję wskazuje się jawnie przez `uproszczony`.
 *
 * Logo poziome nie schodzi poniżej 120 px szerokości (README pakietu):
 * mniejsza `wysokosc` jest podnoszona do minimum, a rozmiar z klasy CSS
 * pilnuje `min-height` w `marka.css`. Reguła jest mechanizmem, nie prośbą —
 * pierwsze wdrożenie podpisu stawiało 32 px, czyli 119 px szerokości.
 *
 * Komponent jest serwerowy (bez hooków) — wolno go wołać z `not-found.tsx`
 * i z layoutów Next bez dyrektywy `'use client'`.
 */
export type UkladLogo = 'poziome' | 'pionowe' | 'sygnet' | 'napis';

export interface LogoInsolitumProps extends Omit<
  SVGAttributes<SVGSVGElement>,
  'width' | 'height' | 'viewBox' | 'children'
> {
  uklad?: UkladLogo;
  /** Wysokość w pikselach. Bez niej rozmiar ustala klasa CSS. */
  wysokosc?: number;
  /**
   * Nazwa dostępna dla czytnika ekranu. `null` = znak dekoracyjny
   * (`aria-hidden`) — gdy obok stoi już tekst „Insolitum".
   */
  tytul?: string | null;
  /**
   * Wersja uproszczona sygnetu. Domyślnie wynika z `wysokosc` (< 32 px);
   * jawna wartość ma pierwszeństwo — dla rozmiaru ustalanego klasą CSS.
   */
  uproszczony?: boolean;
}

/** Wysokość, poniżej której sygnet przechodzi na wersję uproszczoną. */
export const PROG_SYGNETU_UPROSZCZONEGO = 32;

const SCIEZKA_SYGNETU = sciezkaSygnetu();
const SCIEZKA_SYGNETU_UPROSZCZONEGO = sciezkaSygnetuUproszczonego();

export const LogoInsolitum = forwardRef<SVGSVGElement, LogoInsolitumProps>(
  (
    { uklad = 'poziome', wysokosc, tytul = 'Insolitum', uproszczony, className, style, ...props },
    ref
  ) => {
    const prosty =
      uklad === 'sygnet' &&
      (uproszczony ?? (wysokosc !== undefined && wysokosc < PROG_SYGNETU_UPROSZCZONEGO));
    const wysokoscZnaku =
      uklad === 'poziome' && wysokosc !== undefined
        ? Math.max(wysokosc, MIN_WYSOKOSC_POZIOMEGO_PX)
        : wysokosc;

    let szer: number;
    let wys: number;
    let tresc: JSX.Element;

    switch (uklad) {
      case 'sygnet':
        if (prosty) {
          szer = wys = SYGNET_UPROSZCZONY.siatka;
          tresc = <path d={SCIEZKA_SYGNETU_UPROSZCZONEGO} />;
        } else {
          szer = wys = SIATKA_SYGNETU;
          tresc = <path d={SCIEZKA_SYGNETU} />;
        }
        break;
      case 'napis':
        szer = NAPIS_SZEROKOSC;
        wys = NAPIS_WYSOKOSC;
        tresc = <path d={SCIEZKA_NAPISU} />;
        break;
      default: {
        const u = UKLADY_LOGO[uklad];
        szer = u.szerokosc;
        wys = u.wysokosc;
        tresc = (
          <>
            <path d={SCIEZKA_SYGNETU} transform={`translate(${u.sygnet.x} ${u.sygnet.y})`} />
            <path d={SCIEZKA_NAPISU} transform={`translate(${u.napis.x} ${u.napis.y})`} />
          </>
        );
      }
    }

    const dekoracyjne = tytul === null;
    return (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${szer} ${wys}`}
        height={wysokoscZnaku}
        width={wysokoscZnaku !== undefined ? (wysokoscZnaku * szer) / wys : undefined}
        fill="currentColor"
        role={dekoracyjne ? undefined : 'img'}
        aria-label={dekoracyjne ? undefined : tytul}
        aria-hidden={dekoracyjne ? true : undefined}
        focusable="false"
        className={cn('ids-logo', `ids-logo--${uklad}`, className)}
        // Proporcja z siatki układu: przy rozmiarze z klasy (sama wysokość)
        // przeglądarka liczy szerokość z niej, a nie z domyślnych 300 × 150.
        style={{ aspectRatio: `${szer} / ${wys}`, ...style }}
        data-uklad={uklad}
        {...props}
      >
        {tresc}
      </svg>
    );
  }
);

LogoInsolitum.displayName = 'LogoInsolitum';
